import {SCHEMA_TYPES, type SchemaType} from '../../../features/allspicejson.ts';
import type {TreeNode, Value} from '../../../components/inspector_tree.ts';
import {PAD_HOLES, UNKNOWN_LAYER, VIA_HOLES} from '../../stores/layers.ts';

export type TreeNodeWithPage = TreeNode & {
  page?: string;
};

export type DiffOverlaySuppressedTarget = {
  pageId: string | null;
  selector: string;
};

export type NodeInfo = {
  name: string;
  description?: string;
};

function headOrBaseVal(node: TreeNode, keyPath: string) {
  const keyParts = keyPath.split('.');
  let currentNode = node;
  for (const part of keyParts) {
    currentNode = currentNode.childNode(part);
    if (!currentNode) {
      return null;
    }
  }

  if (!currentNode.primitive) {
    throw new Error('Cannot call headOrBaseVal on a non-primitive node');
  }

  return (currentNode.head as Value).val ?? (currentNode.base as Value).val ?? null;
}

function fallbackNodeName(node: TreeNode, name: string | null | undefined) {
  if (name) {
    return name;
  }

  if (typeof node?.childNode !== 'function') {
    return null;
  }

  return headOrBaseVal(node, 'name') || null;
}

function hasSignificantChanges(node: TreeNode, isSignificantlyChanged: (node: TreeNode) => boolean): boolean {
  // if the node wasn't changed at all, there are no changes to be significant
  if (!(node.modified || node.added || node.removed)) {
    return false;
  }

  if (node.children.length > 0) {
    return node.children.some((child) => hasSignificantChanges(child, isSignificantlyChanged));
  }
  return isSignificantlyChanged(node);
}

function isSignificantSchematicLeafChange(node: TreeNode): boolean {
  return !(
    node.key === 'net_id' ||
    node.key === 'font_id' ||
    node.key.endsWith('_font_id') ||
    node.key === 'layer_id' ||
    node.key.endsWith('_layer_id')
  );
}

function isNonVisualOnlyChange(node: TreeNode): boolean {
  return node.modified && !(node.added || node.removed) && !hasSignificantChanges(node, isSignificantSchematicLeafChange);
}

const SINGLE_PAGE_SCHEMATIC_GROUP_TYPES = ['components', 'ports', 'wires', 'junctions'] as const;
const SINGLE_PAGE_SCHEMATIC_GRAPHICS_TYPES = ['texts', 'bitmaps', 'arcs', 'beziers', 'ellipses', 'rectangles', 'polygons', 'polylines'] as const;

type GroupedChanges = Record<string, TreeNodeWithPage[]>;
type SchemaOperations = {
  groupChanges: (root: TreeNode) => GroupedChanges;
  formatGroupName: (groupName: string) => string;
  getNodeLayers?: (node: TreeNode, groupName: string, root: TreeNode) => number[];
  formatNodeInfo: (node: TreeNodeWithPage, groupName: string) => NodeInfo;
  getSelectorForEntityId: (id: string, groupName: string) => string;
};

const altiumSchematic: SchemaOperations = {
  groupChanges: (root) => {
    const changedNodes = root.children
      .filter((node) => {
        // we do not care about changes in these top-level nodes
        if (['title', 'revision', 'variantname', 'documentname', 'html_id', 'font_id', 'net_id', 'variants'].includes(node.key)) {
          return false;
        }

        // we do not care about nodes that didn't change
        if (!(node.added || node.removed || node.modified)) {
          return false;
        }

        // we do not care about nodes where only non-visual ids changed
        if (node.modified && !(node.added || node.removed)) {
          return hasSignificantChanges(node, isSignificantSchematicLeafChange);
        }

        return true;
      });

    return Object.groupBy(changedNodes, (node) => headOrBaseVal(node, 'type'));
  },
  formatGroupName: (groupName) => {
    switch (groupName) {
      case 'SheetRef':
        return 'Sheet Reference';
      default:
        return groupName;
    }
  },
  formatNodeInfo: (node, groupName) => {
    switch (groupName) {
      case 'Component':
        return {
          name: `${headOrBaseVal(node, 'name')} (${headOrBaseVal(node, 'reference')})`,
          description: headOrBaseVal(node, 'description'),
        };
      case 'SheetRef':
        return {
          name: headOrBaseVal(node, 'sheet_name.name'),
          description: headOrBaseVal(node, 'filename'),
        };
      case 'Wire':
        return {
          name: 'Wire',
        };
      case 'Label': // fall through
      case 'NetLabel': // fall through
      case 'Port': // fall through
      case 'PowerPort':
        return {
          name: headOrBaseVal(node, 'name'),
        };
      default:
        return {
          name: null,
          description: null,
        };
    }
  },
  getSelectorForEntityId: (id, _groupName) => {
    const selector = `[data-id='${id}']:not(tspan)`;
    return selector;
  },
};

const pcb: SchemaOperations = {
  groupChanges: (root) => {
    const groups: Record<string, TreeNode[]> = {
      component_instances: [],
      arcs: [],
      tracks: [],
      pads: [],
      vias: [],
      texts: [],
      regions: [],
      padstacks: [],
    };

    for (const groupType of Object.keys(groups)) {
      const childNodes = root.childNode(groupType)?.children ?? [];
      const changedGroupNodes = childNodes
        .filter((node) => node.added || node.removed || node.modified);
      groups[groupType].push(...changedGroupNodes);
    }

    return groups;
  },
  formatGroupName: (groupName) => groupName === 'component_instances' ? 'components' : groupName,
  getNodeLayers: (node, groupName, root) => {
    let layers: number[] = [];

    if (groupName === 'component_instances') {
      layers = pullLayersFromComponentInstance(node, root);
    } else if (groupName === 'pads') {
      layers = pullLayersFromPad(node, root);
    } else if (groupName === 'vias') {
      layers = pullLayersFromVia(node, root);
    } else if (groupName === 'padstacks') {
      layers = pullLayersFromPadstack(node);
    } else {
      const layer = Number(headOrBaseVal(node, 'layer_id'));
      layers = layer === null ? [] : [layer];
    }

    // the Unknown layer never shows up, so we shouldn't see it when asking what layers a node is on
    return layers.filter((layer) => layer !== UNKNOWN_LAYER.id);
  },
  formatNodeInfo: (node, groupName) => {
    switch (groupName) {
      case 'component_instances':
        return {
          name: headOrBaseVal(node, 'designator'),
        };
      case 'texts':
        return {
          name: headOrBaseVal(node, 'text'),
        };
      case 'tracks':
        return {
          name: 'track',
        };
      case 'regions':
        return {
          name: 'region',
        };
      default:
        return {
          name: null,
        };
    }
  },
  getSelectorForEntityId: (id, groupName) => {
    // in PCBs, <tspan>s are relative to their <text> parents and so throw off bounds calculations — hence, we exclude them
    const selector = groupName === 'component_instances' ? `[data-path^='component_instances/${id}']:not(tspan)` : `[data-id='${id}']:not(tspan)`;
    return selector;
  },
};

function pullLayersFromComponentInstance(componentInstance: TreeNode, root: TreeNode): number[] {
  const subcomponentLayerIds = [
    ...(componentInstance.childNode('arcs')?.children ?? []),
    ...(componentInstance.childNode('texts')?.children ?? []),
    ...(componentInstance.childNode('tracks')?.children ?? []),
  ].map((sc) => Number(headOrBaseVal(sc, 'layer_id')));

  const padLayerIds = (componentInstance.childNode('pads')?.children ?? []).flatMap((pad) => pullLayersFromPad(pad, root));
  const viaLayerIds = (componentInstance.childNode('vias')?.children ?? []).flatMap((pad) => pullLayersFromVia(pad, root));

  const layers = new Set(subcomponentLayerIds.concat(padLayerIds, viaLayerIds));
  return Array.from(layers).filter((layer) => layer !== null);
}

function pullLayersFromPadstack(padstack: TreeNode): number[] {
  const layers = padstack.childNode('layers')?.children.map((child) => Number(child.key)) ?? [];
  return layers;
}

function pullLayersFromVia(via: TreeNode, root: TreeNode): number[] {
  const padstackId = headOrBaseVal(via, 'padstack');
  const padstack = root.childNode('padstacks')?.childNode(padstackId);
  if (!padstack) {
    console.warn(`Could not find referenced padstack ${padstackId}`);
    return [];
  }

  const layers = pullLayersFromPadstack(padstack);
  if (padstack.childNode('hole')) {
    layers.push(VIA_HOLES.id);
  }

  return layers;
}

function pullLayersFromPad(pad: TreeNode, root: TreeNode): number[] {
  const padstackId = headOrBaseVal(pad, 'padstack');
  const padstack = root.childNode('padstacks')?.childNode(padstackId);
  if (!padstack) {
    console.warn(`Could not find referenced padstack ${padstackId}`);
    return [];
  }

  const layers = pullLayersFromPadstack(padstack);
  if (padstack.childNode('hole')) {
    layers.push(PAD_HOLES.id);
  }

  return layers;
}

type GroupsAndGraphics = {
  groups: Record<string, TreeNode[]>;
  graphics: Record<string, TreeNode[]>;
};

function createEmptySchematicGroupsAndGraphics(): GroupsAndGraphics {
  return {
    groups: {
      components: [],
      ports: [],
      wires: [],
      junctions: [],
    },
    graphics: {
      texts: [],
      bitmaps: [],
      arcs: [],
      beziers: [],
      ellipses: [],
      rectangles: [],
      polygons: [],
      polylines: [],
    },
  };
}

function groupSchematicEntities(pageRoot: TreeNode, groupsAndGraphics: GroupsAndGraphics = null, pageId: string | null = null) {
  groupsAndGraphics = groupsAndGraphics ?? createEmptySchematicGroupsAndGraphics();

  for (const groupType of SINGLE_PAGE_SCHEMATIC_GROUP_TYPES) {
    const changedGroupNodes = pageRoot.childNode(groupType)?.children
      .filter((node) => {
        // we do not care about nodes that didn't change
        if (!(node.added || node.removed || node.modified)) {
          return false;
        }

        // we do not care about nodes where only non-visual ids changed
        if (node.modified && !(node.added || node.removed)) {
          return hasSignificantChanges(node, isSignificantSchematicLeafChange);
        }

        return true;
      }) ?? [];

    if (pageId) {
      for (const node of changedGroupNodes as TreeNodeWithPage[]) {
          // TODO: there should probably be some better way to do this
        node.page = pageId;
      }
    }

    groupsAndGraphics.groups[groupType].push(...changedGroupNodes);
  }

  // graphics are nested inside the graphics key instead of being top-level
  for (const graphicsType of SINGLE_PAGE_SCHEMATIC_GRAPHICS_TYPES) {
    const changedGroupNodes = pageRoot.childNode('graphics')?.childNode(graphicsType)?.children
      .filter((node) => {
        // we do not care about nodes that didn't change
        if (!(node.added || node.removed || node.modified)) {
          return false;
        }

        // we do not care about nodes where only non-visual ids changed
        if (node.modified && !(node.added || node.removed)) {
          return hasSignificantChanges(node, isSignificantSchematicLeafChange);
        }

        return true;
      }) ?? [];

    if (pageId) {
      for (const node of changedGroupNodes as TreeNodeWithPage[]) {
          // TODO: there should probably be some better way to do this
        node.page = pageId;
      }
    }

    groupsAndGraphics.graphics[graphicsType].push(...changedGroupNodes);
  }

  return groupsAndGraphics;
}

const singlePageSchematic: SchemaOperations = {
  groupChanges: (root) => {
    const {groups, graphics} = groupSchematicEntities(root);

    return {...groups, ...graphics};
  },
  formatGroupName: (groupName) => groupName === 'texts' ? 'text' : groupName,
  formatNodeInfo: (node, group) => {
    switch (group) {
      case 'components': {
        const nodeName = headOrBaseVal(node, 'name');
        const nodeReference = headOrBaseVal(node, 'reference');

        let name = null;
        if (nodeName && nodeReference) {
          name = `${nodeName} (${nodeReference})`;
        } else {
          name = nodeName || nodeReference || null;
        }

        return {
          name,
          description: headOrBaseVal(node, 'description') ?? headOrBaseVal(node, 'attributes.DESCRIPTION.value') ?? null,
        };
      }
      case 'ports':
        return {
          name: headOrBaseVal(node, 'name') || null,
          description: null,
        };
      case 'texts':
        return {
          name: headOrBaseVal(node, 'text') || null,
          description: null,
        };
      case 'wires':
        return {
          name: 'wire',
        };
      default:
        return {
          name: null,
          description: null,
        };
    }
  },
  getSelectorForEntityId: (id, groupName) => {
    if (['components', 'ports'].includes(groupName)) {
      return `g[data-id='${id}']`;
    }

    return `[data-id='${id}']:not(tspan)`;
  },
};

function collectSuppressedSchematicTargets(pageRoot: TreeNode, pageId: string | null = null): DiffOverlaySuppressedTarget[] {
  const targets: DiffOverlaySuppressedTarget[] = [];

  for (const groupType of SINGLE_PAGE_SCHEMATIC_GROUP_TYPES) {
    const nodes = pageRoot.childNode(groupType)?.children ?? [];
    for (const node of nodes) {
      if (!isNonVisualOnlyChange(node)) {
        continue;
      }

      targets.push({
        pageId,
        selector: singlePageSchematic.getSelectorForEntityId(node.key, groupType),
      });
    }
  }

  for (const graphicsType of SINGLE_PAGE_SCHEMATIC_GRAPHICS_TYPES) {
    const nodes = pageRoot.childNode('graphics')?.childNode(graphicsType)?.children ?? [];
    for (const node of nodes) {
      if (!isNonVisualOnlyChange(node)) {
        continue;
      }

      targets.push({
        pageId,
        selector: singlePageSchematic.getSelectorForEntityId(node.key, graphicsType),
      });
    }
  }

  return targets;
}

const schematic: SchemaOperations = {
  groupChanges: (root) => {
    const pagesNode = root.childNode('pages');
    if (!pagesNode) {
      // Single-page schematic (no pages wrapper) — fall back to flat grouping.
      return singlePageSchematic.groupChanges(root);
    }

    let groupsAndGraphics: GroupsAndGraphics = null;

    const pageNodes = pagesNode.children;
    for (const pageNode of pageNodes) {
      const pageId = headOrBaseVal(pageNode, 'id');
      groupsAndGraphics = groupSchematicEntities(pageNode, groupsAndGraphics, pageId);
    }

    return {...groupsAndGraphics.groups, ...groupsAndGraphics.graphics};
  },
  formatGroupName: singlePageSchematic.formatGroupName,
  formatNodeInfo: singlePageSchematic.formatNodeInfo,
  getSelectorForEntityId: singlePageSchematic.getSelectorForEntityId,
};

const schemaOperations: Record<SchemaType, SchemaOperations> = {
  [SCHEMA_TYPES.ALTIUM_SCHEMATIC]: altiumSchematic,
  [SCHEMA_TYPES.PCB]: pcb,
  [SCHEMA_TYPES.PCB_COMPONENT_LIB]: null,
  [SCHEMA_TYPES.SCHEMATIC]: schematic,
  [SCHEMA_TYPES.SCHEMATIC_COMPONENT_LIB]: null,
  [SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC]: singlePageSchematic,
  [SCHEMA_TYPES.UNKNOWN]: null,
};

export function groupChanges(schemaType: SchemaType, diffTree: TreeNode) {
  return schemaOperations[schemaType]?.groupChanges(diffTree) ?? {};
}

export function makePageNameMap(diffTree: TreeNode) {
  // this one seems schema type-agnostic so we won't delegate it
  const pageNameMap: Record<string, string> = {};

  const pageNodes = diffTree.childNode('pages')?.children ?? [];
  for (const pageNode of pageNodes) {
    const pageId = headOrBaseVal(pageNode, 'id');
    const pageName = headOrBaseVal(pageNode, 'name');
    pageNameMap[pageId] = pageName;
  }

  return pageNameMap;
}

export function formatGroupName(schemaType: SchemaType, groupName: string) {
  return schemaOperations[schemaType]?.formatGroupName(groupName) ?? 'changes';
}

export function getNodeLayers(schemaType: SchemaType, node: TreeNodeWithPage, groupName: string, root: TreeNodeWithPage) {
  return schemaOperations[schemaType]?.getNodeLayers?.(node, groupName, root) ?? [];
}

export function formatNodeInfo(schemaType: SchemaType, node: TreeNodeWithPage, groupName: string) {
  const info = schemaOperations[schemaType]?.formatNodeInfo(node, groupName) ?? {name: null};

  return {
    ...info,
    name: fallbackNodeName(node, info.name),
  };
}

export function getSelectorForEntityId(schemaType: SchemaType, id: string, groupName: string) {
  return schemaOperations[schemaType]?.getSelectorForEntityId(id, groupName) ?? null;
}

export function collectDiffOverlaySuppressedTargets(schemaType: SchemaType, diffTree: TreeNode): DiffOverlaySuppressedTarget[] {
  if (schemaType === SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC) {
    return collectSuppressedSchematicTargets(diffTree);
  }

  if (schemaType === SCHEMA_TYPES.SCHEMATIC) {
    const pagesNode = diffTree.childNode('pages');
    if (!pagesNode) {
      return collectSuppressedSchematicTargets(diffTree);
    }

    return pagesNode.children.flatMap((pageNode) => {
      const pageId = headOrBaseVal(pageNode, 'id');
      return collectSuppressedSchematicTargets(pageNode, typeof pageId === 'string' ? pageId : null);
    });
  }

  return [];
}
